import React, { useEffect, useMemo, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import DobDayField from "@app/components/dob/DobDayField";
import DobMonthField from "@app/components/dob/DobMonthField";
import DobYearField from "@app/components/dob/DobYearField";
import { getMonthsMap, MonthsMapOption } from "@app/components/dob/months";
import { getLowerYearLimit } from "@app/utils/dobHelper";
import {
  ClearValidationError,
  SetValidationError,
} from "@natera/platform/lib/hooks/useError";
import "@app/components/forms/dobVerificationForm/dobVerificationForm.scss";
import { parseDateOnly } from "@app/utils/convertDate";
import { useDobValidation } from "@app/hooks/useDobValidation";

import "./dobInput.scss";

interface DobInputProps {
  getValidationError: (field: string) => string | undefined;
  setValidationError: SetValidationError;
  clearValidationError: ClearValidationError;
  onChange?: (dateValue: string) => void;
  defaultValue?: string;
  showLabels?: boolean;
  validateAge?: boolean;
  disabled?: boolean;
}

const messages = defineMessages({
  dateOfBirthMonthLabel: {
    id: "dateOfBirthVerificationMonthLabel",
    defaultMessage: "Month",
  },
  dateOfBirthDayLabel: {
    id: "dateOfBirthVerificationDayLabel",
    defaultMessage: "Day",
  },
  dateOfBirthYearLabel: {
    id: "dateOfBirthVerificationYearLabel",
    defaultMessage: "Year",
  },
  dateOfBirthInvalidYear: {
    id: "setupProfileFormInvalidDateOfBirth",
    defaultMessage: "Incorrect date of birth. Please re-enter",
  },
  dateOfBirthUnder18: {
    id: "setupProfileFormUnder18DateOfBirth",
    defaultMessage:
      "The patient portal does not currently have test information for patients under 18 years old. Please reach out to your provider, or contact support@natera.com, for information on tests for patients under 18.",
  },
  dateOfBirthVerificationInvalidValue: {
    id: "dateOfBirthVerificationInvalidValue",
    defaultMessage: "Invalid or incomplete value.",
  },
  dateOfBirthRequired: {
    id: "setupProfileFormRequired",
    defaultMessage: "This information is required.",
  },
});

const DobInput: React.FC<DobInputProps> = ({
  getValidationError,
  setValidationError,
  clearValidationError,
  onChange,
  defaultValue,
  showLabels = false,
  disabled = false,
  validateAge = true,
}) => {
  const intl = useIntl();

  const currentYear = new Date().getFullYear();
  const lowerYearLimit = getLowerYearLimit();

  const monthsMap = useMemo(() => getMonthsMap(intl), [intl]);

  const [selectedMonth, setSelectedMonth] = useState<MonthsMapOption | null>(
    null
  );
  const [selectedDay, setSelectedDay] = useState<number | undefined>(undefined);
  const [year, setYear] = useState<string>("");
  const [dayInputValue, setDayInputValue] = useState<string>("");

  useEffect(() => {
    if (defaultValue) {
      const date = parseDateOnly(defaultValue);

      if (!isNaN(date.getTime())) {
        const yearValue = date.getFullYear().toString();
        const monthValue = monthsMap[date.getMonth()];
        const dayValue = date.getDate();

        setYear(yearValue);
        setSelectedMonth(monthValue);
        setSelectedDay(dayValue);
        setDayInputValue(dayValue.toString().padStart(2, "0"));
      }
    }
  }, [defaultValue, monthsMap]);

  useDobValidation(selectedMonth, selectedDay, year, {
    setValidationError,
    clearValidationError,
    invalidDayMessage: intl.formatMessage(
      messages.dateOfBirthVerificationInvalidValue
    ),
    under18Message: intl.formatMessage(messages.dateOfBirthUnder18),
    validateAge,
    lowerYearLimit,
    currentYear,
    onValidFullDate: (dateValue) => {
      clearValidationError("dateOfBirth");
      clearValidationError("dateOfBirthMonth");
      clearValidationError("dateOfBirthDay");
      clearValidationError("dateOfBirthYear");
      clearValidationError("dateOfBirthIncorrect");
      clearValidationError("dateOfBirthRequired");
      onChange?.(dateValue);
    },
  });

  return (
    <div className="dob-input dob-verification-form">
      <input
        type="hidden"
        name="dateOfBirth"
        value={
          selectedMonth && selectedDay && year && year.length === 4
            ? `${year}-${selectedMonth.value.toString().padStart(2, "0")}-${selectedDay.toString().padStart(2, "0")}`
            : ""
        }
      />
      <div className="dob-verification-form__row">
        <DobMonthField
          monthsMap={monthsMap}
          selectedMonth={selectedMonth}
          setSelectedMonth={setSelectedMonth}
          selectedDay={selectedDay}
          selectedYear={year}
          getValidationError={getValidationError}
          setValidationError={setValidationError}
          clearValidationError={clearValidationError}
          showLabel={showLabels}
          disabled={disabled}
        />

        <DobDayField
          selectedMonth={selectedMonth}
          selectedYear={year}
          setSelectedDay={setSelectedDay}
          setDayInputValue={setDayInputValue}
          dayInputValue={dayInputValue}
          getValidationError={getValidationError}
          setValidationError={setValidationError}
          clearValidationError={clearValidationError}
          showLabel={showLabels}
          disabled={disabled}
        />
      </div>
      <div className="dob-verification-form__year">
        <DobYearField
          setYear={setYear}
          year={year}
          lowerYearLimit={lowerYearLimit}
          currentYear={currentYear}
          getValidationError={getValidationError}
          setValidationError={setValidationError}
          clearValidationError={clearValidationError}
          showLabel={showLabels}
          disabled={disabled}
        />
      </div>
    </div>
  );
};

export default DobInput;
